import { useMemo } from "react";

import type { Schools, POIs } from "@local-logic/client";
import type { ScoreKey } from "~/sdks/LocalContent/types";

import { usePois } from "~/features/clientLibrary/usePois";
import { useSchools } from "~/features/clientLibrary/useSchools";

import {
  DEFAULT_RADIUS,
  DEFAULT_NUMBER_OF_POIS,
} from "~/sdks/LocalContent/constants";

import { categories } from "./constants";

type Options = {
  lat?: number;
  lng?: number;
  category?: ScoreKey;
  radius?: number;
};

const schools = Object.keys(categories).filter((key) =>
  key.includes("schools")
);

export const useMarkers = ({
  lat,
  lng,
  category,
  radius = DEFAULT_RADIUS,
}: Options) => {
  const { isLoading: isPoisLoading, data: poisData } = usePois({
    lat,
    lng,
    include:
      category && !schools.includes(category)
        ? (categories[category] as unknown as (keyof POIs)[])
        : undefined,
    limit: DEFAULT_NUMBER_OF_POIS,
    attemptMinimum: 3,
    radius,
  });

  const { isLoading: isSchoolsLoading, data: schoolsData } = useSchools({
    lat,
    lng,
    include:
      category && schools.includes(category)
        ? (categories[category] as unknown as (keyof Schools)[])
        : undefined,
    limit: DEFAULT_NUMBER_OF_POIS,
    attemptMinimum: 3,
    radius,
    includeCatchments: true,
  });

  const markers = useMemo(() => {
    if (typeof category === "string" && schools.includes(category)) {
      const filteredResponse: Partial<Schools> = {};

      if (category === "high_schools") {
        filteredResponse.high_schools = schoolsData?.high_schools || [];
      }

      if (category === "primary_schools") {
        filteredResponse.primary_schools = schoolsData?.primary_schools || [];
      }

      return filteredResponse as Schools;
    }

    return poisData;
  }, [poisData, schoolsData, category]);

  return {
    isLoading: isPoisLoading || isSchoolsLoading,
    data: markers as Partial<POIs & Schools>,
  };
};
